/**
 * Controls shared by the two T&C template editors (Quote Terms, Check-In Terms).
 *
 * The two admin pages are deliberate near-twins — the check-in one was modelled
 * on the quote one — and both grew markdown and templating in the same change.
 * Rather than let the copies drift, the parts that are identical live here: the
 * format toggle, the variables panel, the unknown-placeholder warning, and the
 * insert-at-cursor helper behind it.
 *
 * What stays in each page is what genuinely differs: which registry it shows
 * and what sample data its preview renders against.
 */

import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  TERMS_BODY_FORMATS,
  findUnknownTemplatePaths,
  type TermsBodyFormat,
} from '@techgitter/contracts';

import { CARD_SX } from '../../styles/designSystem';

/** The shape both registries share — quote and check-in variables alike. */
export interface TemplateVariableEntry {
  path: string;
  label: string;
  example: string;
  source: string;
}

const FORMAT_LABEL: Record<TermsBodyFormat, string> = {
  text: 'Plain text',
  markdown: 'Markdown',
};

const FORMAT_HELP: Record<TermsBodyFormat, string> = {
  text: 'Rendered exactly as typed, line breaks preserved. What every template written before markdown support uses.',
  markdown:
    '**bold**, *italic*, - bullets, 1. numbers, # headings, > quotes, --- rules and [links](https://…). Anything else shows as typed.',
};

export interface TermsFormatToggleProps {
  value: TermsBodyFormat;
  onChange: (next: TermsBodyFormat) => void;
  /** Shown when switching an existing template that customers already see. */
  warnOnSwitch?: boolean;
}

/**
 * Plain text ↔ markdown.
 *
 * Deliberately explicit rather than inferred from the body: a clause beginning
 * "- we are not liable…" is a bullet to a markdown parser and a sentence to
 * its author, and guessing wrong reflows a document a customer may already
 * have accepted.
 */
export const TermsFormatToggle: React.FC<TermsFormatToggleProps> = ({
  value,
  onChange,
  warnOnSwitch,
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: '#646464' }}>Format</Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(_e, next) => next && onChange(next as TermsBodyFormat)}
      >
        {TERMS_BODY_FORMATS.map((format) => (
          <Tooltip key={format} title={FORMAT_HELP[format]} placement="top">
            <ToggleButton value={format} sx={{ px: 1.5, py: 0.25, fontSize: 12 }}>
              {FORMAT_LABEL[format]}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
    {warnOnSwitch && value === 'markdown' && (
      <Typography sx={{ fontSize: 12, color: '#946200', mt: 0.75 }}>
        Check the preview before saving — markdown reflows lines that begin with
        <code> - </code>, <code> * </code> or <code> 1. </code>.
      </Typography>
    )}
  </Box>
);

export interface UnknownVariableWarningProps {
  /** Bodies to scan — typically the body and the closer. */
  sources: (string | null | undefined)[];
  variables: readonly TemplateVariableEntry[];
}

/**
 * Flags `{{typos}}` the registry does not know.
 *
 * A warning, never a block: an unknown placeholder renders empty at runtime,
 * and a body may legitimately be written against a variable a newer deployment
 * adds. What it prevents is the silent case — a misspelt path that renders as
 * nothing at all in a customer's terms and looks like a missing sentence.
 */
export const UnknownVariableWarning: React.FC<UnknownVariableWarningProps> = ({
  sources,
  variables,
}) => {
  const unknown = useMemo(() => {
    const known = variables.map((v) => v.path);
    const found = new Set<string>();
    for (const source of sources) {
      for (const path of findUnknownTemplatePaths(source, known)) {
        found.add(path);
      }
    }
    return [...found].sort();
  }, [sources, variables]);

  if (unknown.length === 0) return null;

  return (
    <Alert severity="warning" sx={{ fontSize: 12, py: 0.5 }}>
      {unknown.length === 1 ? 'This variable is' : 'These variables are'} not in
      the list below and will render as nothing:{' '}
      {unknown.map((path) => (
        <code key={path} style={{ marginRight: 8 }}>{`{{${path}}}`}</code>
      ))}
    </Alert>
  );
};

export interface VariablesPanelProps {
  variables: readonly TemplateVariableEntry[];
  /** Source groups in the order they should be listed. */
  groups: readonly string[];
  /** Called with `{{path}}` when a row is clicked. */
  onInsert?: (snippet: string) => void;
}

/** The "Available variables" reference, one row per variable. */
export const VariablesPanel: React.FC<VariablesPanelProps> = ({
  variables,
  groups,
  onInsert,
}) => {
  const grouped = useMemo(() => {
    const out: Record<string, TemplateVariableEntry[]> = {};
    for (const group of groups) out[group] = [];
    for (const v of variables) (out[v.source] ??= []).push(v);
    return out;
  }, [variables, groups]);

  return (
    <Box sx={[CARD_SX, { p: 2 }]}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
        Available variables
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#888', display: 'block', mb: 1.5 }}>
        Use <code>{'{{path}}'}</code> for interpolation and{' '}
        <code>{'{{#if path}}…{{/if}}'}</code> for conditional blocks.
        {onInsert ? ' Click a variable to insert it at the cursor.' : ''}
      </Typography>
      {groups.map(
        (group) =>
          (grouped[group]?.length ?? 0) > 0 && (
            <Box key={group} sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#646464',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 0.5,
                }}
              >
                {group}
              </Typography>
              <Box
                component="table"
                sx={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}
              >
                <tbody>
                  {grouped[group].map((v) => (
                    <Box
                      component="tr"
                      key={v.path}
                      onClick={onInsert ? () => onInsert(`{{${v.path}}}`) : undefined}
                      sx={
                        onInsert
                          ? {
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#f5f8fc' },
                            }
                          : undefined
                      }
                    >
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                        <code>{`{{${v.path}}}`}</code>
                      </td>
                      <td style={{ padding: '4px 8px', color: '#333' }}>{v.label}</td>
                      <td style={{ padding: '4px 8px', color: '#888' }}>
                        {v.example}
                      </td>
                    </Box>
                  ))}
                </tbody>
              </Box>
            </Box>
          ),
      )}
    </Box>
  );
};

/**
 * Insert `snippet` at the cursor of a textarea, or at the end when the field
 * has never been focused.
 *
 * Returns the new value; the caller owns the state. The selection is restored
 * after the inserted text so a run of clicks reads as typing, not as each
 * click fighting the last.
 */
export function insertAtCursor(
  field: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  snippet: string,
): string {
  if (!field) return `${value}${snippet}`;

  const start = field.selectionStart ?? value.length;
  const end = field.selectionEnd ?? value.length;
  const next = `${value.slice(0, start)}${snippet}${value.slice(end)}`;

  // The DOM value is replaced by React on the next render, so move the caret
  // afterwards or it snaps back to the start of the field.
  window.requestAnimationFrame(() => {
    field.focus();
    const caret = start + snippet.length;
    field.setSelectionRange(caret, caret);
  });

  return next;
}
